import { useEffect, useRef } from 'react'
import Hls from 'hls.js'
import mux from 'mux-embed'
import type { Asset } from '@/organisms/AngelPlayer/BitmovinPlayer/types'
import { getMuxKey } from '@/utils/environment-variables'
import { logger } from '@/utils/logging'

interface UseHlsMuxOptions {
  videoRef: React.RefObject<HTMLVideoElement | null>
  hlsRef: React.RefObject<Hls | null>
  isReady: boolean
  asset: Asset
  autoplay?: boolean
  anonymousUserId?: string
  loggedInUserId?: string | null
  sessionId?: string
}

/**
 * Monitors an HLS.js video element with the Mux Data SDK for analytics.
 *
 * Initializes Mux monitoring when the video + HLS.js instance are ready,
 * emits `videochange` events when the asset URL changes, and cleans up
 * on unmount. Mirrors the metadata shape used by the Bitmovin Mux integration.
 */
export function useHlsMux({
  videoRef,
  hlsRef,
  isReady,
  asset,
  autoplay,
  anonymousUserId,
  loggedInUserId,
  sessionId,
}: UseHlsMuxOptions): void {
  const playerInitTimeRef = useRef(Date.now())
  const monitoredRef = useRef(false)
  const loadedUrlRef = useRef<string | null>(null)

  // Initialize Mux monitoring once when HLS.js is ready
  useEffect(() => {
    const video = videoRef.current
    const hls = hlsRef.current
    if (!video || !hls || !isReady || monitoredRef.current) return

    const envKey = getMuxKey()
    if (!envKey) {
      logger().warn('Mux analytics: NEXT_PUBLIC_MUX_KEY not set, skipping initialization')
      return
    }

    if (!sessionId) {
      logger().warn('Mux analytics initialized without sessionId', {
        sessionId,
        userId: loggedInUserId,
        anonymousId: anonymousUserId,
      })
    }

    mux.monitor(video, {
      debug: false,
      hlsjs: hls,
      Hls,
      data: {
        env_key: envKey,
        player_name: 'angel-web',
        player_version: Hls.version,
        player_init_time: playerInitTimeRef.current,
        player_autoplay_on: autoplay,
        viewer_user_id: anonymousUserId,
        custom_1: loggedInUserId || undefined,
        // TODO: when web implements user profiles, use that profile id here instead (custom_2).
        custom_2: loggedInUserId || undefined,
        custom_3: sessionId || undefined,
        ...getMuxVideoMetadata(asset),
      },
    })

    monitoredRef.current = true
    loadedUrlRef.current = asset.sourceConfig.hls

    return () => {
      if (monitoredRef.current && video) {
        mux.destroyMonitor(video)
        monitoredRef.current = false
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when HLS.js signals ready
  }, [isReady, videoRef.current, hlsRef.current])

  // Emit videochange when asset URL changes (e.g., switching episodes)
  useEffect(() => {
    if (!monitoredRef.current) return
    const video = videoRef.current
    if (!video) return

    const newUrl = asset.sourceConfig.hls
    if (newUrl === loadedUrlRef.current) return
    loadedUrlRef.current = newUrl

    mux.emit(video, 'videochange', getMuxVideoMetadata(asset))
  }, [asset, videoRef])
}

function getMuxVideoMetadata(asset: Asset) {
  return {
    video_id: asset.analytics.content_id,
    video_series: asset.projectSlug,
    video_title: asset.sourceConfig.subtitle,
    video_content_type: asset.analytics.videoType,
    video_duration: asset.analytics.total_length ? asset.analytics.total_length * 1000 : undefined,
  }
}
